/* eslint-disable max-statements */
import { Box, Divider, Flex, Group, Title } from '@mantine/core';
import React, { useEffect, useRef, useState } from 'react';

interface ContentLayoutFC extends React.FC<{ children: React.ReactNode; 'data-pw'?: string }> {
  Title: React.FC<{ children: React.ReactNode; 'data-pw'?: string }>;
  Filters: React.FC<{ children: React.ReactNode; 'data-pw'?: string }>;
  Header: React.FC<{ children: React.ReactNode; 'data-pw'?: string }>;
  SubHeader: React.FC<{ children: React.ReactNode; 'data-pw'?: string }>;
  Content: React.FC<{ children: React.ReactNode; 'data-pw'?: string; showDivider?: boolean }>;
}

export const ContentLayout: ContentLayoutFC = ({ children, 'data-pw': dataPw }) => {
  return (
    <Flex direction="column" mt="xl" h={'100%'} data-pw={dataPw}>
      {children}
    </Flex>
  );
};

ContentLayout.Title = ({ children, 'data-pw': dataPw }) => {
  return (
    <Title order={2} data-pw={dataPw}>
      {children}
    </Title>
  );
};

ContentLayout.Filters = ({ children, 'data-pw': dataPw }) => {
  return <Group data-pw={dataPw}>{children}</Group>;
};

ContentLayout.Header = ({ children, 'data-pw': dataPw }) => {
  return (
    <Box id="contentLayoutHeader" p="xl" data-pw={dataPw}>
      <Group justify="space-between" align="center">
        {children}
      </Group>
    </Box>
  );
};

ContentLayout.SubHeader = ({ children, 'data-pw': dataPw }) => {
  return (
    <Box id="contentLayoutSubHeader" px="lg" data-pw={dataPw}>
      {children}
    </Box>
  );
};

const Content = ({ children, 'data-pw': dataPw, showDivider = true }: { children: React.ReactNode; 'data-pw'?: string; showDivider?: boolean }) => {
  const headerRef = useRef<HTMLElement | null>(null);
  const subHeaderRef = useRef<HTMLElement | null>(null);
  const [contentHeight, setContentHeight] = useState('auto');

  useEffect(() => {
    headerRef.current = document.getElementById('contentLayoutHeader');
    subHeaderRef.current = document.getElementById('contentLayoutSubHeader');

    const calculateHeight = () => {
      let totalOffset = (headerRef.current?.offsetHeight ?? 0) + (subHeaderRef.current?.offsetHeight ?? 0);

      totalOffset += 1; // 1px for divider

      setContentHeight(`calc(100% - ${totalOffset}px)`);
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(calculateHeight);

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    if (subHeaderRef.current) {
      resizeObserver.observe(subHeaderRef.current);
    }

    window.addEventListener('resize', calculateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  return (
    <>
      {showDivider && <Divider />}
      <Box p="md" pt={0} h={contentHeight} pos="relative" style={{ overflow: 'auto' }} data-pw={dataPw}>
        {children}
      </Box>
    </>
  );
};

ContentLayout.Content = Content;
